import { isNumberedSectionHeading, stripLinePrefix, stripMarkdownBold, flattenSubjectiveStoryLabels, coalesceNarrativeParagraphs, joinLinesToParagraphs, formatMedicationsAsBullets } from './soap-section-format.util';

export interface SoapHeadingSpec {
  /** Stable id for UI keys */
  id: string;
  /** Display title in the Visits expandable UI (mock-aligned where applicable). */
  title: string;
  /** Match against a heading line after stripping number/bullet prefixes. */
  match: RegExp;
}

/** Subjective (S) components from soap-components-format / backend output. */
export const SUBJECTIVE_HEADING_SPECS: SoapHeadingSpec[] = [
  { id: 'identifying', title: 'Identifying Information', match: /^identifying\s+information\b/i },
  { id: 'cc', title: 'Chief Complaint', match: /^chief\s+complaint/i },
  { id: 'hpi', title: 'History of Present Illness', match: /^history\s+of\s+present\s+illness|\bhpi\b/i },
  { id: 'functional', title: 'Functional Status', match: /^functional\s+status/i },
  { id: 'cognitive', title: 'Cognitive/Behavioral Symptoms', match: /^cognitive\b/i },
  { id: 'pain', title: 'Pain Inventory', match: /^pain\s+inventory\b/i },
  { id: 'sleep', title: 'Sleep History', match: /^sleep\s+history\b/i },
  { id: 'diet', title: 'Diet/Nutrition', match: /^diet\b|^nutrition\b/i },
  { id: 'hydration', title: 'Hydration', match: /^hydration\b/i },
  { id: 'bowel', title: 'Bowel/Bladder', match: /^bowel\b|^bladder\b/i },
  { id: 'pmh', title: 'Past Medical History', match: /^past\s+medical\s+history|\bpmh\b|^medical\s+history\b|^patient\s+presentation\b/i },
  { id: 'psh', title: 'Past Surgical History', match: /^past\s+surgical\s+history|\bpsh\b/i },
  { id: 'meds', title: 'Medications & supplements', match: /^medications?\b/i },
  { id: 'allergies', title: 'Allergies', match: /^allergies\b/i },
  { id: 'family', title: 'Family History', match: /^family\s+history\b/i },
  { id: 'social', title: 'Social History', match: /^social\s+history\b/i },
  { id: 'perspective', title: 'Patient Perspective', match: /^patient\s+perspective\b/i },
  { id: 'ros', title: 'Review of Systems', match: /^review\s+of\s+systems|\bros\b/i },
];

export interface SplitSoapSection {
  id: string;
  title: string;
  body: string;
}

/**
 * Split a SOAP field (subjective/objective) into expandable sections by numbered
 * or known headings. Only returns sections that have non-empty body text.
 * If no headings are found, returns a single fallback section when text is present.
 */
export function splitSoapByHeadings(
  text: string | null | undefined,
  specs: SoapHeadingSpec[],
  fallback?: { id: string; title: string }
): SplitSoapSection[] {
  const raw = (text ?? '').replace(/\r/g, '').trim();
  if (!raw || /^n\/a$/i.test(raw)) {
    return [];
  }

  const lines = raw.split('\n');
  const sections: { spec: SoapHeadingSpec | null; title: string; lines: string[] }[] = [];
  let current: { spec: SoapHeadingSpec | null; title: string; lines: string[] } | null = null;
  let preamble: string[] = [];

  const startSection = (title: string, spec: SoapHeadingSpec | null) => {
    current = { spec, title, lines: [] };
    sections.push(current);
  };

  for (const line of lines) {
    const heading = matchHeadingLine(line, specs);
    if (heading) {
      startSection(heading.spec?.title ?? heading.rawTitle, heading.spec);
      continue;
    }
    if (!current) {
      preamble.push(line);
      continue;
    }
    current.lines.push(line);
  }

  // Orphan text before the first heading — attach to first section or fallback.
  const preambleBody = preamble.join('\n').trim();
  if (preambleBody) {
    if (sections.length > 0) {
      sections[0].lines = [...preamble, ...sections[0].lines];
    } else if (fallback) {
      return [{ id: fallback.id, title: fallback.title, body: preambleBody }];
    }
  }

  const out: SplitSoapSection[] = [];
  const seen = new Set<string>();

  for (const section of sections) {
    const body = section.lines.join('\n').trim();
    if (!body) {
      continue;
    }
    const baseId = section.spec?.id ?? slugify(section.title);
    let id = baseId;
    let n = 2;
    while (seen.has(id)) {
      id = `${baseId}-${n++}`;
    }
    seen.add(id);
    out.push({ id, title: section.title, body });
  }

  if (!out.length && fallback) {
    return [{ id: fallback.id, title: fallback.title, body: raw }];
  }

  return out;
}

/**
 * Visits display: one "History of Present Illness" accordion with four inner
 * headings (Chief Complaint, HPI, Medications & supplements, Review of Systems).
 * Other Subjective parts are folded into the HPI narrative.
 */
export function foldSubjectiveIntoHpiAndRos(sections: SplitSoapSection[]): SplitSoapSection[] {
  if (!sections.length) {
    return [];
  }

  const ccParts: string[] = [];
  const hpiParts: string[] = [];
  const medsParts: string[] = [];
  const rosParts: string[] = [];

  for (const section of sections) {
    const body = flattenSubjectiveStoryLabels(stripKnownSubjectiveSubheadings(section.body));
    if (!body) {
      continue;
    }
    if (section.id === 'cc') {
      ccParts.push(body);
    } else if (section.id === 'meds') {
      medsParts.push(body);
    } else if (section.id === 'ros') {
      rosParts.push(body);
    } else {
      hpiParts.push(body);
    }
  }

  const chunks: string[] = [];
  appendHpiChunk(chunks, 'Chief Complaint', ccParts);
  appendHpiChunk(chunks, 'History of Present Illness', hpiParts);
  appendHpiChunk(chunks, 'Medications & supplements', medsParts);
  appendHpiChunk(chunks, 'Review of Systems', rosParts);

  const hpiBody = flattenSubjectiveStoryLabels(chunks.join('\n\n')).replace(/\n{3,}/g, '\n\n').trim();
  if (!hpiBody) {
    return [];
  }
  return [{ id: 'hpi', title: 'History of Present Illness', body: hpiBody }];
}

function appendHpiChunk(chunks: string[], heading: string, parts: string[]): void {
  let body = parts.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
  if (!body) {
    return;
  }
  if (heading === 'History of Present Illness') {
    body = coalesceNarrativeParagraphs(body, 4);
  } else if (heading === 'Medications & supplements') {
    body = formatMedicationsAsBullets(body);
  } else {
    body = joinLinesToParagraphs(body);
  }
  chunks.push(`${heading}\n${body}`);
}

function stripKnownSubjectiveSubheadings(body: string): string {
  return (body ?? '')
    .replace(/\r/g, '')
    .split('\n')
    .filter((line) => !isBareKnownHeading(line, SUBJECTIVE_HEADING_SPECS))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function matchHeadingLine(
  line: string,
  specs: SoapHeadingSpec[]
): { spec: SoapHeadingSpec | null; rawTitle: string } | null {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }

  // Numbered ("1. Chief complaint") or bulletized ("• Chief complaint") headings.
  const looksNumbered = isNumberedSectionHeading(trimmed);
  const looksBulletHeading = /^•\s+\S/.test(trimmed);
  if (!looksNumbered && !looksBulletHeading && !isBareKnownHeading(trimmed, specs)) {
    return null;
  }

  let title = stripMarkdownBold(stripLinePrefix(trimmed));
  // Drop trailing colon on heading-only lines.
  title = title.replace(/:\s*$/, '').trim();
  if (!title || /[.?!]$/.test(title)) {
    return null;
  }

  const spec = specs.find((s) => s.match.test(title)) ?? null;
  if (!spec && !looksNumbered && !looksBulletHeading) {
    return null;
  }

  // Avoid treating long narrative lines that happen to start with a number as headings.
  if (!spec && title.length > 90) {
    return null;
  }

  return { spec, rawTitle: title };
}

function isBareKnownHeading(line: string, specs: SoapHeadingSpec[]): boolean {
  const title = stripMarkdownBold(stripLinePrefix(line)).replace(/:\s*$/, '').trim();
  if (!title || title.length > 90 || /[.?!]$/.test(title)) {
    return false;
  }
  const words = title.split(/\s+/).filter(Boolean).length;
  if (words < 1 || words > 12) {
    return false;
  }
  return specs.some((s) => s.match.test(title));
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'section';
}
